// src/components/gallery/SharePopup.tsx
// Komponen popup share modern & estetik (Editorial Ivory, Cinematic Noir, Archival Frame, & Foto Asli)
// Desain bersih, tipografi elegan (serif), tanpa gradasi berlebihan / AI slop, dan fokus pada judul.

import React, { useState, useEffect } from 'react';
import {
  X,
  Share2,
  MessageCircle,
  Instagram,
  Copy,
  Download,
  Check,
  Image as ImageIcon,
} from 'lucide-react';

export type ShareTemplateType = 'editorial_light' | 'editorial_dark' | 'polaroid' | 'original';

interface SharePopupProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  imageTitle: string;
  imageDescription?: string;
  authorName?: string;
  authorHandle?: string;
  authorAvatar?: string;
}

const SharePopup: React.FC<SharePopupProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageTitle,
  authorName = 'Evano Rama Atharrazka',
  authorHandle = '@evano.rama',
}) => {
  const [activeTemplate, setActiveTemplate] = useState<ShareTemplateType>('editorial_light');
  const [isProcessing, setIsProcessing] = useState(false);
  const [copiedType, setCopiedType] = useState<'text' | 'link' | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const pageUrl = typeof window !== 'undefined' ? window.location.origin + '/profile' : '';

  useEffect(() => {
    if (isOpen) {
      setActiveTemplate('editorial_light');
      setCopiedType(null);
      setStatusMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const showTempStatus = (msg: string) => {
    setStatusMessage(msg);
    setTimeout(() => {
      setStatusMessage(null);
    }, 4000);
  };

  // Helper untuk menggambar rounded rect pada canvas
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) => {
    ctx.beginPath();
    if (typeof (ctx as any).roundRect === 'function') {
      (ctx as any).roundRect(x, y, w, h, r);
    } else {
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
    }
    ctx.closePath();
  };

  // Helper untuk text wrapping pada canvas tanpa tumpang tindih
  const getWrappedLines = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxLines: number = 3
  ): string[] => {
    const words = text.trim().split(/\s+/);
    const lines: string[] = [];
    let currentLine = '';

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = ctx.measureText(testLine).width;

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
        if (lines.length === maxLines - 1) {
          // Baris terakhir yang diperbolehkan
          let remaining = words.slice(i).join(' ');
          while (ctx.measureText(remaining + '…').width > maxWidth && remaining.length > 0) {
            const lastSpace = remaining.lastIndexOf(' ');
            remaining = lastSpace > 0 ? remaining.substring(0, lastSpace) : remaining.slice(0, -1);
          }
          lines.push(remaining ? `${remaining}…` : '…');
          return lines;
        }
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine && lines.length < maxLines) {
      lines.push(currentLine);
    }

    return lines;
  };

  // Generator Canvas Estetik & Minimalis (Editorial Design)
  const generateAestheticImage = async (template: ShareTemplateType): Promise<File | null> => {
    try {
      // 1. Mode Foto Asli: unduh gambar murni
      if (template === 'original') {
        const response = await fetch(imageUrl, { mode: 'cors' });
        const blob = await response.blob();
        const ext = blob.type.split('/')[1] || 'jpg';
        const safeName = imageTitle.replace(/[^a-zA-Z0-9-_]/g, '_').toLowerCase() || 'foto_vano';
        return new File([blob], `${safeName}.${ext}`, { type: blob.type || 'image/jpeg' });
      }

      // Pastikan font dimuat sebelum menggambar
      try {
        await Promise.all([
          document.fonts.load('600 36px "Playfair Display"'),
          document.fonts.load('500 20px "Plus Jakarta Sans"'),
        ]);
      } catch {
        // Fallback jika fonts.load tidak didukung browser tertentu
      }

      // 2. Load foto utama
      const mainImg = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = imageUrl;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const cleanTitle = imageTitle.trim();

      // ==========================================
      // TEMPLATE 1: EDITORIAL IVORY (Story 9:16)
      // Bersih, hangat, minimalis ala galeri seni
      // ==========================================
      if (template === 'editorial_light') {
        canvas.width = 1080;
        canvas.height = 1920;

        // Solid background warm ivory
        ctx.fillStyle = '#F7F6F2';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Header: Subtle uppercase title
        ctx.save();
        ctx.fillStyle = '#78716C'; // warm stone grey
        ctx.font = '500 20px "Plus Jakarta Sans", -apple-system, sans-serif';
        if ('letterSpacing' in ctx) {
          (ctx as any).letterSpacing = '4px';
        }
        ctx.textAlign = 'center';
        ctx.fillText(authorName.toUpperCase(), canvas.width / 2, 145);
        ctx.restore();

        // Foto Utama (Framed Passpartout)
        const cardX = 90;
        const cardY = 210;
        const cardW = 900;
        const cardH = 1180;

        // Soft, natural shadow
        ctx.save();
        ctx.shadowColor = 'rgba(28, 25, 23, 0.08)';
        ctx.shadowBlur = 32;
        ctx.shadowOffsetY = 14;
        ctx.fillStyle = '#FFFFFF';
        drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 20);
        ctx.fill();
        ctx.restore();

        // Draw photo clipped inside
        ctx.save();
        drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 20);
        ctx.clip();

        const scale = Math.max(cardW / mainImg.naturalWidth, cardH / mainImg.naturalHeight);
        const w = mainImg.naturalWidth * scale;
        const h = mainImg.naturalHeight * scale;
        const x = cardX + (cardW - w) / 2;
        const y = cardY + (cardH - h) / 2;
        ctx.drawImage(mainImg, x, y, w, h);
        ctx.restore();

        // Delicate 1px fine border
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.06)';
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 20);
        ctx.stroke();

        // Tipografi Judul (Hanya Judul - Editorial Serif)
        let fontSize = 42;
        let lineHeight = 58;
        if (cleanTitle.length > 55) {
          fontSize = 32;
          lineHeight = 46;
        } else if (cleanTitle.length > 30) {
          fontSize = 36;
          lineHeight = 52;
        }

        ctx.save();
        ctx.font = `600 ${fontSize}px "Playfair Display", Georgia, "Times New Roman", serif`;
        ctx.fillStyle = '#1C1917'; // Rich warm black
        ctx.textAlign = 'center';

        const lines = getWrappedLines(ctx, cleanTitle, 880, 3);
        const totalTextHeight = lines.length * lineHeight;
        const bottomAreaStart = cardY + cardH; // 1390
        const bottomAreaEnd = 1760;
        const availableHeight = bottomAreaEnd - bottomAreaStart;
        const startY = bottomAreaStart + (availableHeight - totalTextHeight) / 2 + lineHeight / 2;

        lines.forEach((line, index) => {
          ctx.fillText(line, canvas.width / 2, startY + index * lineHeight);
        });
        ctx.restore();

        // Footer Minimalis
        ctx.save();
        ctx.fillStyle = '#A8A29E';
        ctx.font = '500 18px "Plus Jakarta Sans", -apple-system, sans-serif';
        if ('letterSpacing' in ctx) {
          (ctx as any).letterSpacing = '3px';
        }
        ctx.textAlign = 'center';
        ctx.fillText(`PORTFOLIO  •  ${authorHandle}`, canvas.width / 2, 1800);
        ctx.restore();
      }

      // ==========================================
      // TEMPLATE 2: CINEMATIC NOIR (Story 9:16)
      // Matte charcoal luxury, minimalis, dan elegan
      // ==========================================
      else if (template === 'editorial_dark') {
        canvas.width = 1080;
        canvas.height = 1920;

        // Solid background matte black/charcoal
        ctx.fillStyle = '#121316';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Header: Subtle uppercase title
        ctx.save();
        ctx.fillStyle = '#A1A1AA'; // soft zinc
        ctx.font = '500 20px "Plus Jakarta Sans", -apple-system, sans-serif';
        if ('letterSpacing' in ctx) {
          (ctx as any).letterSpacing = '4px';
        }
        ctx.textAlign = 'center';
        ctx.fillText(authorName.toUpperCase(), canvas.width / 2, 145);
        ctx.restore();

        // Foto Utama
        const cardX = 90;
        const cardY = 210;
        const cardW = 900;
        const cardH = 1180;

        // Shadow
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
        ctx.shadowBlur = 40;
        ctx.shadowOffsetY = 16;
        ctx.fillStyle = '#1C1D22';
        drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 20);
        ctx.fill();
        ctx.restore();

        // Photo draw
        ctx.save();
        drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 20);
        ctx.clip();

        const scale = Math.max(cardW / mainImg.naturalWidth, cardH / mainImg.naturalHeight);
        const w = mainImg.naturalWidth * scale;
        const h = mainImg.naturalHeight * scale;
        const x = cardX + (cardW - w) / 2;
        const y = cardY + (cardH - h) / 2;
        ctx.drawImage(mainImg, x, y, w, h);
        ctx.restore();

        // Fine border
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 20);
        ctx.stroke();

        // Tipografi Judul (Hanya Judul - Editorial Serif Off-White)
        let fontSize = 42;
        let lineHeight = 58;
        if (cleanTitle.length > 55) {
          fontSize = 32;
          lineHeight = 46;
        } else if (cleanTitle.length > 30) {
          fontSize = 36;
          lineHeight = 52;
        }

        ctx.save();
        ctx.font = `600 ${fontSize}px "Playfair Display", Georgia, "Times New Roman", serif`;
        ctx.fillStyle = '#F4F4F5'; // Off-white
        ctx.textAlign = 'center';

        const lines = getWrappedLines(ctx, cleanTitle, 880, 3);
        const totalTextHeight = lines.length * lineHeight;
        const bottomAreaStart = cardY + cardH;
        const bottomAreaEnd = 1760;
        const availableHeight = bottomAreaEnd - bottomAreaStart;
        const startY = bottomAreaStart + (availableHeight - totalTextHeight) / 2 + lineHeight / 2;

        lines.forEach((line, index) => {
          ctx.fillText(line, canvas.width / 2, startY + index * lineHeight);
        });
        ctx.restore();

        // Footer Minimalis
        ctx.save();
        ctx.fillStyle = '#71717A';
        ctx.font = '500 18px "Plus Jakarta Sans", -apple-system, sans-serif';
        if ('letterSpacing' in ctx) {
          (ctx as any).letterSpacing = '3px';
        }
        ctx.textAlign = 'center';
        ctx.fillText(`PORTFOLIO  •  ${authorHandle}`, canvas.width / 2, 1800);
        ctx.restore();
      }

      // ==========================================
      // TEMPLATE 3: ARCHIVAL FRAME (Feed 4:5)
      // Desain bingkai kertas seni minimalis
      // ==========================================
      else if (template === 'polaroid') {
        canvas.width = 1080;
        canvas.height = 1350;

        // Background netral studio
        ctx.fillStyle = '#EFECE6';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Archival White Card
        const polX = 80;
        const polY = 70;
        const polW = 920;
        const polH = 1210;

        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
        ctx.shadowBlur = 28;
        ctx.shadowOffsetY = 10;
        ctx.fillStyle = '#FFFFFF';
        drawRoundedRect(ctx, polX, polY, polW, polH, 16);
        ctx.fill();
        ctx.restore();

        // Area Foto
        const photoMargin = 45;
        const pW = polW - photoMargin * 2; // 830
        const pH = 840;
        const pTop = polY + photoMargin;

        ctx.save();
        drawRoundedRect(ctx, polX + photoMargin, pTop, pW, pH, 10);
        ctx.clip();

        const pScale = Math.max(pW / mainImg.naturalWidth, pH / mainImg.naturalHeight);
        const imgW = mainImg.naturalWidth * pScale;
        const imgH = mainImg.naturalHeight * pScale;
        ctx.drawImage(
          mainImg,
          polX + photoMargin + (pW - imgW) / 2,
          pTop + (pH - imgH) / 2,
          imgW,
          imgH
        );
        ctx.restore();

        // 1px border tipis foto
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.06)';
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, polX + photoMargin, pTop, pW, pH, 10);
        ctx.stroke();

        // Tipografi Judul (Hanya Judul)
        let fontSize = 36;
        let lineHeight = 50;
        if (cleanTitle.length > 55) {
          fontSize = 28;
          lineHeight = 40;
        } else if (cleanTitle.length > 30) {
          fontSize = 32;
          lineHeight = 44;
        }

        ctx.save();
        ctx.font = `600 ${fontSize}px "Playfair Display", Georgia, serif`;
        ctx.fillStyle = '#1C1917';
        ctx.textAlign = 'center';

        const lines = getWrappedLines(ctx, cleanTitle, pW - 40, 2);
        const totalTextHeight = lines.length * lineHeight;
        const bottomCardTop = pTop + pH;
        const bottomCardBottom = polY + polH - 55;
        const startY = bottomCardTop + (bottomCardBottom - bottomCardTop - totalTextHeight) / 2 + lineHeight / 2;

        lines.forEach((line, index) => {
          ctx.fillText(line, canvas.width / 2, startY + index * lineHeight);
        });
        ctx.restore();

        // Handle di bawah
        ctx.save();
        ctx.fillStyle = '#94A3B8';
        ctx.font = '500 16px "Plus Jakarta Sans", sans-serif';
        if ('letterSpacing' in ctx) {
          (ctx as any).letterSpacing = '2px';
        }
        ctx.textAlign = 'center';
        ctx.fillText(authorHandle, canvas.width / 2, polY + polH - 35);
        ctx.restore();
      }

      // Convert canvas ke File
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png', 0.96)
      );

      if (!blob) return null;
      const safeName = cleanTitle.replace(/[^a-zA-Z0-9-_]/g, '_').toLowerCase() || 'foto_vano';
      return new File([blob], `${safeName}_${template}.png`, { type: 'image/png' });
    } catch {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new File([blob], 'foto-vano.jpg', { type: blob.type || 'image/jpeg' });
      } catch {
        return null;
      }
    }
  };

  // Salin teks ke clipboard
  const copyTextToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    }
  };

  // Salin Tautan
  const handleCopyLink = async () => {
    const textToCopy = `${imageTitle}\n\nPortofolio ${authorName}: ${pageUrl}`;
    await copyTextToClipboard(textToCopy);
    setCopiedType('link');
    showTempStatus('Tautan portofolio berhasil disalin!');
    setTimeout(() => setCopiedType(null), 2500);
  };

  // Download File Gambar
  const triggerDownloadFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // Bagikan ke WhatsApp
  const handleWhatsAppShare = async () => {
    setIsProcessing(true);
    const shareText = `${imageTitle}\n\nPortofolio: ${pageUrl}`;
    try {
      const imageFile = await generateAestheticImage(activeTemplate);

      // Web Share API (HP / Browser modern)
      if (
        imageFile &&
        typeof navigator !== 'undefined' &&
        navigator.canShare &&
        navigator.canShare({ files: [imageFile] })
      ) {
        await navigator.share({
          files: [imageFile],
          title: imageTitle,
          text: shareText,
        });
        showTempStatus('Menu berbagi berhasil dibuka!');
        return;
      }

      // Desktop fallback
      if (imageFile) {
        triggerDownloadFile(imageFile);
      }
      await copyTextToClipboard(shareText);

      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
      window.open(whatsappUrl, '_blank');
      showTempStatus('Gambar diunduh & membuka WhatsApp...');
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(whatsappUrl, '_blank');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Bagikan ke Instagram
  const handleInstagramShare = async () => {
    setIsProcessing(true);
    const shareText = `${imageTitle}\n\nPortofolio: ${pageUrl}`;
    try {
      const imageFile = await generateAestheticImage(activeTemplate);

      // Web Share API langsung ke Instagram di HP
      if (
        imageFile &&
        typeof navigator !== 'undefined' &&
        navigator.canShare &&
        navigator.canShare({ files: [imageFile] })
      ) {
        await navigator.share({
          files: [imageFile],
          title: imageTitle,
        });
        showTempStatus('Membuka menu share Instagram...');
        return;
      }

      // Desktop fallback: unduh gambar Full HD + buka Instagram
      if (imageFile) {
        triggerDownloadFile(imageFile);
      }
      await copyTextToClipboard(shareText);

      showTempStatus('Gambar estetik berhasil diunduh! Silakan unggah ke Instagram.');
      setTimeout(() => {
        window.open('https://www.instagram.com/', '_blank');
      }, 1000);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        showTempStatus('Gagal membagikan gambar.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Unduh Gambar HD Sesuai Template
  const handleDownload = async () => {
    setIsProcessing(true);
    try {
      const imageFile = await generateAestheticImage(activeTemplate);
      if (imageFile) {
        triggerDownloadFile(imageFile);
        showTempStatus('Gambar berhasil diunduh dalam kualitas Full HD!');
      } else {
        window.open(imageUrl, '_blank');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto animate-in fade-in slide-in-from-bottom-4 duration-200 border border-neutral-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Modal - Clean & Minimalist */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 sticky top-0 bg-white/95 backdrop-blur-md z-20">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-neutral-900">
              Bagikan Gambar
            </h3>
            <p className="text-xs text-neutral-500">
              Pilih gaya tampilan minimalis untuk dibagikan atau diunduh
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-100 rounded-full transition-colors cursor-pointer text-neutral-400 hover:text-neutral-700"
            aria-label="Tutup"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body Modal */}
        <div className="p-5 space-y-4">
          {/* Pemilih Template (Clean Editorial Tabs) */}
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {/* Ivory Light */}
              <button
                type="button"
                onClick={() => setActiveTemplate('editorial_light')}
                className={`py-2 px-2.5 rounded-xl border text-xs font-medium flex flex-col items-center gap-1 transition-all cursor-pointer ${
                  activeTemplate === 'editorial_light'
                    ? 'border-neutral-900 bg-neutral-900 text-white shadow-xs'
                    : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <div className="w-5 h-7 rounded-xs border border-current flex items-center justify-center">
                  <span className="text-[7px]">9:16</span>
                </div>
                <span>Ivory (Story)</span>
              </button>

              {/* Noir Dark */}
              <button
                type="button"
                onClick={() => setActiveTemplate('editorial_dark')}
                className={`py-2 px-2.5 rounded-xl border text-xs font-medium flex flex-col items-center gap-1 transition-all cursor-pointer ${
                  activeTemplate === 'editorial_dark'
                    ? 'border-neutral-900 bg-neutral-900 text-white shadow-xs'
                    : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <div className="w-5 h-7 rounded-xs bg-current p-0.5 flex items-center justify-center">
                  <div className={`w-3.5 h-5 rounded-3xs ${activeTemplate === 'editorial_dark' ? 'bg-neutral-900' : 'bg-white'}`} />
                </div>
                <span>Noir (Story)</span>
              </button>

              {/* Archival Frame 4:5 */}
              <button
                type="button"
                onClick={() => setActiveTemplate('polaroid')}
                className={`py-2 px-2.5 rounded-xl border text-xs font-medium flex flex-col items-center gap-1 transition-all cursor-pointer ${
                  activeTemplate === 'polaroid'
                    ? 'border-neutral-900 bg-neutral-900 text-white shadow-xs'
                    : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <div className="w-6 h-7 rounded-xs border border-current p-0.5 flex flex-col justify-between">
                  <div className="w-full h-4 bg-current/20 rounded-3xs" />
                </div>
                <span>Frame (4:5)</span>
              </button>

              {/* Foto Asli */}
              <button
                type="button"
                onClick={() => setActiveTemplate('original')}
                className={`py-2 px-2.5 rounded-xl border text-xs font-medium flex flex-col items-center gap-1 transition-all cursor-pointer ${
                  activeTemplate === 'original'
                    ? 'border-neutral-900 bg-neutral-900 text-white shadow-xs'
                    : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <ImageIcon className="h-6 w-6 mt-0.5" />
                <span>Foto Asli</span>
              </button>
            </div>
          </div>

          {/* Live Aesthetic Preview Window */}
          <div className="relative rounded-xl overflow-hidden border border-neutral-200 shadow-xs">
            {/* TEMPLATE PREVIEW: IVORY */}
            {activeTemplate === 'editorial_light' && (
              <div className="bg-[#F7F6F2] h-72 flex flex-col justify-between p-4 select-none">
                {/* Header */}
                <div className="text-center">
                  <span className="text-[9px] font-medium tracking-[3px] text-stone-500 uppercase">
                    {authorName}
                  </span>
                </div>

                {/* Center photo */}
                <div className="mx-auto w-40 h-36 rounded-lg overflow-hidden shadow-sm border border-black/5 bg-white">
                  <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                </div>

                {/* Bottom title & handle */}
                <div className="text-center space-y-1 px-4">
                  <h4 className="font-serif text-sm font-semibold text-stone-900 line-clamp-2 leading-snug">
                    {imageTitle}
                  </h4>
                  <p className="text-[9px] tracking-[2px] text-stone-400 uppercase">
                    PORTFOLIO • {authorHandle}
                  </p>
                </div>
              </div>
            )}

            {/* TEMPLATE PREVIEW: NOIR */}
            {activeTemplate === 'editorial_dark' && (
              <div className="bg-[#121316] h-72 flex flex-col justify-between p-4 select-none">
                {/* Header */}
                <div className="text-center">
                  <span className="text-[9px] font-medium tracking-[3px] text-zinc-400 uppercase">
                    {authorName}
                  </span>
                </div>

                {/* Center photo */}
                <div className="mx-auto w-40 h-36 rounded-lg overflow-hidden shadow-md border border-white/10 bg-zinc-900">
                  <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                </div>

                {/* Bottom title & handle */}
                <div className="text-center space-y-1 px-4">
                  <h4 className="font-serif text-sm font-semibold text-zinc-100 line-clamp-2 leading-snug">
                    {imageTitle}
                  </h4>
                  <p className="text-[9px] tracking-[2px] text-zinc-500 uppercase">
                    PORTFOLIO • {authorHandle}
                  </p>
                </div>
              </div>
            )}

            {/* TEMPLATE PREVIEW: POLAROID / ARCHIVAL */}
            {activeTemplate === 'polaroid' && (
              <div className="bg-[#EFECE6] h-72 flex items-center justify-center p-4 select-none">
                <div className="bg-white p-3 pb-3.5 rounded-lg shadow-sm border border-neutral-200/60 w-48 text-center">
                  <div className="h-36 bg-neutral-100 rounded-xs overflow-hidden mb-2 border border-black/5">
                    <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                  <h4 className="font-serif text-xs font-semibold text-neutral-900 line-clamp-2 leading-snug">
                    {imageTitle}
                  </h4>
                  <p className="text-[8px] tracking-wider text-neutral-400 mt-1 uppercase">
                    {authorHandle}
                  </p>
                </div>
              </div>
            )}

            {/* TEMPLATE PREVIEW: ORIGINAL */}
            {activeTemplate === 'original' && (
              <div className="relative h-72 bg-neutral-100 flex items-center justify-center overflow-hidden select-none">
                <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-neutral-900/70 text-white text-[10px]">
                  Foto Asli
                </span>
              </div>
            )}
          </div>

          {/* Judul Gambar (Only Title, No Caption) */}
          <div className="p-3 bg-neutral-50 rounded-xl border border-neutral-200/70">
            <div className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider mb-1">
              Judul
            </div>
            <div className="font-serif text-sm font-semibold text-neutral-900 leading-snug">
              {imageTitle}
            </div>
          </div>

          {/* Status Feedback Notification */}
          {statusMessage && (
            <div className="p-2.5 rounded-xl bg-neutral-100 border border-neutral-300 text-neutral-800 text-xs font-medium flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-neutral-700 flex-shrink-0" />
              <span>{statusMessage}</span>
            </div>
          )}

          {/* Tombol Aksi Utama */}
          <div className="space-y-2 pt-1">
            {/* Bagikan ke Instagram */}
            <button
              onClick={handleInstagramShare}
              disabled={isProcessing}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-neutral-900 hover:bg-neutral-800 active:scale-[0.99] text-white transition-all cursor-pointer disabled:opacity-60 shadow-xs"
            >
              <div className="flex items-center space-x-3">
                <Instagram className="h-5 w-5 text-white" />
                <div className="text-left">
                  <div className="font-medium text-sm">Bagikan ke Instagram</div>
                  <div className="text-[11px] text-neutral-400">
                    Format gambar estetik (Full HD)
                  </div>
                </div>
              </div>
              <Share2 className="h-4 w-4 text-neutral-400" />
            </button>

            {/* Bagikan ke WhatsApp */}
            <button
              onClick={handleWhatsAppShare}
              disabled={isProcessing}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#128C7E] hover:bg-[#0c6b60] active:scale-[0.99] text-white transition-all cursor-pointer disabled:opacity-60 shadow-xs"
            >
              <div className="flex items-center space-x-3">
                <MessageCircle className="h-5 w-5 text-white" />
                <div className="text-left">
                  <div className="font-medium text-sm">Bagikan ke WhatsApp</div>
                  <div className="text-[11px] text-emerald-100">
                    Kirim gambar estetik beserta judul
                  </div>
                </div>
              </div>
              <Share2 className="h-4 w-4 text-emerald-100" />
            </button>

            {/* Aksi Sekunder: Unduh HD & Salin Tautan */}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={handleDownload}
                disabled={isProcessing}
                className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-neutral-300 hover:bg-neutral-50 active:scale-95 text-neutral-800 text-xs font-semibold transition-all cursor-pointer"
              >
                <Download className="h-4 w-4 text-neutral-600" />
                <span>Unduh Gambar HD</span>
              </button>

              <button
                onClick={handleCopyLink}
                disabled={isProcessing}
                className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-neutral-300 hover:bg-neutral-50 active:scale-95 text-neutral-800 text-xs font-semibold transition-all cursor-pointer"
              >
                {copiedType === 'link' ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-600" />
                    <span className="text-emerald-600">Tersalin!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 text-neutral-600" />
                    <span>Salin Tautan</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePopup;
